import Phaser from 'phaser';
import { COLORS, OUTLINE } from '../theme';

/**
 * Cream circular pad with a pulsing cream ring around it.
 * Sits at the bottom of the play screen — the visual frame around the rest hat.
 *
 * The ring is a thin coral outline that expands outward and fades, then snaps
 * back and repeats — matching the sonar-ping glow under the daily-challenge
 * play button on HomeScene. Replaces the older dashed yolk band, which was
 * busier and competed with the hat for attention.
 */
export class HatPad {
  container: Phaser.GameObjects.Container;
  private scene: Phaser.Scene;
  private ring: Phaser.GameObjects.Graphics;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    size: number,
  ) {
    this.scene = scene;
    this.container = scene.add.container(x, y);
    this.container.setDepth(70);

    // Backplate: cream circle with ink outline + flat shadow underneath.
    const r = size / 2;
    const shadowY = 12;

    const shadow = scene.add.graphics();
    shadow.fillStyle(COLORS.ink, 1);
    shadow.fillCircle(0, shadowY, r);
    this.container.add(shadow);

    const body = scene.add.graphics();
    body.fillStyle(COLORS.cream, 1);
    body.fillCircle(0, 0, r);
    body.lineStyle(OUTLINE, COLORS.ink, 1);
    body.strokeCircle(0, 0, r);
    this.container.add(body);

    // Thin solid cream ring matching the pad body — same beacon role as the
    // daily-card play-button glow on HomeScene. 3px stays readable but doesn't
    // crowd the pad outline; scale 1.25 is the daily-card target.
    this.ring = scene.add.graphics();
    const ringR = r + 14;
    this.ring.lineStyle(3, COLORS.cream, 1);
    this.ring.strokeCircle(0, 0, ringR);
    this.container.add(this.ring);

    scene.tweens.add({
      targets: this.ring,
      scale: 1.25,
      alpha: 0,
      duration: 1200,
      ease: 'Sine.Out',
      repeat: -1,
    });
  }

  /** Kill the infinite ring pulse and destroy the container. Use after a hit
   * so the pad doesn't keep glowing under the TAP TO CONTINUE button. */
  destroy() {
    this.scene.tweens.killTweensOf(this.ring);
    this.container.destroy();
  }
}
